<template>
    <a
        class="l-ss-c-offer"
        :class="{
            'l-ss-c-offer-vertical': isVertical,
            'l-ss-c-offer-mobile': mobile,
            'l-ss-c-offer-adult': isAdult,
            'l-ss-c-offer-not-available': !offer.available,
            [`l-ss-c-offer-${camelToKebab(offerLayout)}`]: true,
        }"
        :data-first-offer-id="offer.id"
        :data-offer-id="currentOffer.id"
        :data-offer-index="offerIndex"
        :href="currentOffer.url"
        :target="linkTargetBlank ? '_blank' : '_self'"
        @click="onclick">
        <offer-item
            v-for="contentItem in offerContentItems"
            :key="contentItem.key"
            :content-item="contentItem"/>
    </a>
</template>

<script>
    import { ref, watch, onBeforeUnmount, onMounted } from '@vue/composition-api';
    import useOfferItemsResolver from '@/core/components/offer/use-offer-items-resolver';

    import useOfferGetPriceController from '@/core/components/offer/offer-price/use-offer-get-price-controller';

    import OfferItem from '@/core/components/offer/offer-item.vue';

    import camelToKebab from '@/core/helpers/camel-to-kebab';

    import { isVertical as checkIsVertical, isMini as checkIsMini } from '@/core/helpers/offer-layout';

    import { nanoid } from 'nanoid';

    export default {
        components: {
            OfferItem,
        },
        props: {
            offer: {
                type: Object,
            },
            settings: {
                type: Object,
                required: true,
            },
            layout: {
                type: String,
                default: 'row',
            },
            offerIndex: {
                type: Number,
                required: true,
            },
            mobile: {
                type: Boolean,
                default: false,
            },
            isAdult: {
                type: Boolean,
                default: false,
            },
            linkTargetBlank: {
                type: Boolean,
                default: false,
            },
            isCartCountAvailable: {
                type: Boolean,
                default: false,
            },
        },
        setup(props, { emit, root }) {
            const { offer, settings } = props;
            const { layout: layoutSettings, cart: cartSettings } = settings;

            const currentOffer = ref(offer);

            const isShowDropdown = ref();

            let offerLayout = props.layout;
            let isVertical = checkIsVertical(offerLayout);
            let isMini = checkIsMini(offerLayout);

            let contentSettings = layoutSettings[offerLayout];

            let destroyComponentsFunc = null;

            const offerCartId = nanoid();

            const emitAddToCartClick = (event) => emit('add-to-cart-click', event);
            const emitRemoveFromCartClick = (event) => emit('remove-from-cart-click', event);
            const emitAddToFavouritesClick = (event) => emit('add-to-favourites-click', event);
            const emitRemoveFromFavouritesClick = (event) => emit('remove-from-favourites-click', event);
            const emitAddToCompareClick = (event) => emit('add-to-compare-click', event);
            const emitRemoveFromCompareClick = (event) => emit('remove-from-compare-click', event);
            const emitLoadGroupedOffers = (event) => emit('load-grouped-offers', event);
            const emitClick = (event) => emit('click', event);

            const offerContentItems = ref([]);

            const initOfferContentItems = (rerender) =>
                useOfferItemsResolver(
                    {
                        items: contentSettings,
                        offer: currentOffer.value,
                        firstOfferId: offerCartId,
                        offerSettings: {
                            ...settings,
                            mobile: props.mobile,
                            vertical: isVertical,
                            isAdult: props.isAdult,
                            isCartCountAvailable: props.isCartCountAvailable,
                            isShowDropdown,
                            customButton: settings.customButton,
                        },
                        on: {
                            addToCartClick: emitAddToCartClick,
                            removeFromCartClick: emitRemoveFromCartClick,
                            addToFavouritesClick: emitAddToFavouritesClick,
                            removeFromFavouritesClick: emitRemoveFromFavouritesClick,
                            addToCompareClick: emitAddToCompareClick,
                            removeFromCompareClick: emitRemoveFromCompareClick,
                            changeOffer: (event) => onChangeOfferByParam(event),
                            loadGroupedOffers: emitLoadGroupedOffers,
                            similarClickHandler: (data) => similarClickHandler(data),
                            customButtonClick: (event) => emit('custom-button-click', event),
                        },
                        root,
                        rerender,
                    });

            const init = (rerender) => {
                const { destroyComponents, contentItems } = initOfferContentItems(rerender);
                offerContentItems.value = contentItems;
                destroyComponentsFunc = destroyComponents;
            };

            onMounted(() => {
                init();
            });

            onBeforeUnmount(() => {
                destroyComponentsFunc();
            });

            const onclick = (event) => {
                if (event.target.closest('.l-ss-c-offer-cart-btn')) {
                    return;
                }

                emitClick(event);
            };

            const updatePrice = async () => {
                const priceData = await useOfferGetPriceController(offer, settings.getPrice);
                currentOffer.value = {
                    ...currentOffer.value,
                    price: priceData.price,
                    oldPrice: priceData.oldPrice,
                };

                init(['price', 'labels']);
            };

            if (settings.getPrice) {
                updatePrice();
            }

            const similarClickHandler = (data) => {
                isShowDropdown.value = data;
            };

            const onChangeOfferByParam = (offer) => {
                currentOffer.value = offer;
                init(true);
            };

            watch(() => props.isAdult, () => {
                init();
            });

            return {
                currentOffer,
                cartSettings,
                offerContentItems,
                isVertical,
                offerLayout,
                isMini,
                onclick,
                camelToKebab,
            };
        },
    };

</script>
